import React from 'react';
import { Link } from 'react-router-dom';
import { useOnline, useRetryWhenBackOnline } from '../hooks/useOnline';
import { usePagedLoad } from '../hooks/usePagedLoad';
import { useRepositories } from '../hooks/useRepositories';
import ListingCard from '../components/ListingCard';
import { ActionError, EmptyState, LoadingState, StateForError } from '../components/PageStates';
import { Button } from '@/components/ui/button';
import { CloudOff, FilePen, Images, Inbox, Plus } from 'lucide-react';

/**
 * `/my-listings` — my scheduled and published listings (for sale, sold and
 * withdrawn, which only I can see), newest first. Drafts live in Drafts,
 * reached from beside New listing, as is New from photos (021 FR-001,
 * FR-011a, FR-011b). The heading and those links stay whatever the list
 * does, so Drafts is reachable offline; the list loads again once back online.
 */
export default function MyListings() {
  const { listings } = useRepositories();
  const { first, items, hasMore, loadMore, loadingMore, moreError } = usePagedLoad(
    (page) => listings.mine(page, 'notDraft'),
    'mine-not-draft',
  );

  const online = useOnline();
  useRetryWhenBackOnline(first.status === 'error', first.reload);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">My listings</h1>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" className="rounded-full" asChild>
            <Link to="/my-listings/drafts">
              <FilePen />
              Drafts
            </Link>
          </Button>
          <Button variant="secondary" className="rounded-full" asChild>
            <Link to="/listings/new-from-photos">
              <Images />
              New from photos
            </Link>
          </Button>
          <Button className="rounded-full" asChild>
            <Link to="/listings/create">
              <Plus />
              New listing
            </Link>
          </Button>
        </div>
      </div>
      {first.status === 'loading' ? (
        <LoadingState label="Loading your listings" />
      ) : first.status === 'error' ? (
        online ? (
          <StateForError error={first.error} onRetry={first.reload} />
        ) : (
          <p className="flex items-center justify-center gap-2 rounded-3xl bg-card px-6 py-10 text-center text-muted-foreground">
            <CloudOff className="size-5 shrink-0" aria-hidden="true" />
            You&apos;re offline. Your listings show here once you&apos;re back online.
          </p>
        )
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Inbox className="size-8" />}
          title="No listings yet"
          description="Listings you post or schedule show up here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((l) => (
            <ListingCard key={l.id} listing={l} />
          ))}
        </div>
      )}
      {moreError !== null && <ActionError error={moreError} />}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
}
